// AuthorizationDecisionDialog.tsx — the full S4 "Record decision" dialog
// (Insurance Claims Phase 4.5.4): outcome, amount, insurer/TPA reference,
// approval-letter upload, and an itemised deductions table with a
// bill-minus-approved reconciliation line. `POST /:id/authorizations/:aid/
// decision` already accepts every field this writes (deductions[],
// external_ref, decision_document_id, counselling_note) — this dialog is
// the first UI to actually offer them.
import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Badge } from '@/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { DocumentUploadField } from './DocumentUploadField';
import { useDecideAuthorization, useCreateQuery } from './hooks';
import { headLabel, byHeadOrder } from './headLabels';
import { CLAIM_HEADS } from '@/features/admin/types';
import type { ClaimAuthorization, ClaimDeduction, ClaimWorkspace } from './types';

const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

type Outcome = 'approved' | 'partial' | 'denied' | 'query';

// Reason codes as they appear on payer letters (wireframe S4); the field
// stays free text — these are suggestions, not a closed list.
const REASON_CODES: Array<[string, string]> = [
  ['NPI', 'Non-payable items (IRDAI list)'],
  ['NMN', 'Not medically necessary'],
];

/** What the claim becomes once this decision is saved — printed on the save
 * button so the desk sees the consequence before committing it. Mirrors
 * routes/claims.js's decision/query handlers. */
function resultingStatus(kind: string, outcome: Outcome): string {
  if (outcome === 'query') return kind === 'final' ? 'Final auth query' : 'Pre-auth query';
  if (outcome === 'denied') return kind === 'initial' ? 'Pre-auth denied → self-pay' : 'Pre-auth approved (cover unchanged)';
  return kind === 'final' ? 'Final approved' : 'Pre-auth approved';
}

interface DeductionRow extends ClaimDeduction { _key: string }

function money(v: number): string {
  return `₹${v.toFixed(2)}`;
}

export interface AuthorizationDecisionDialogProps {
  claim: ClaimWorkspace;
  auth: ClaimAuthorization;
  open: boolean;
  onClose: () => void;
}

export function AuthorizationDecisionDialog({ claim, auth, open, onClose }: AuthorizationDecisionDialogProps) {
  const decideMutation = useDecideAuthorization(claim.id);
  const queryMutation = useCreateQuery(claim.id);
  const [queryText, setQueryText] = useState('');
  const [outcome, setOutcome] = useState<Outcome>('approved');
  const [approvedAmount, setApprovedAmount] = useState(String(auth.requested_amount ?? ''));
  const [externalRef, setExternalRef] = useState('');
  const [decisionDocumentId, setDecisionDocumentId] = useState<number | null>(null);
  const [decisionDocumentName, setDecisionDocumentName] = useState<string | null>(null);
  const [remarks, setRemarks] = useState('');
  const [counsellingNote, setCounsellingNote] = useState('');
  const [deductions, setDeductions] = useState<DeductionRow[]>([]);
  const [error, setError] = useState('');

  const addDeduction = () => setDeductions((d) => [...d, { _key: String(Date.now() + Math.random()), head: '', reason_code: '', description: '', amount: 0 }]);
  const updateDeduction = (key: string, patch: Partial<DeductionRow>) =>
    setDeductions((d) => d.map((row) => (row._key === key ? { ...row, ...patch } : row)));
  const removeDeduction = (key: string) => setDeductions((d) => d.filter((row) => row._key !== key));

  const deductionTotal = round2(deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0));
  const billTotal = claim.running_bill?.net ?? null;
  const amountNum = Number(approvedAmount) || 0;
  const reconciliation = billTotal != null && outcome !== 'denied'
    // The patient side is bill − approved, never negative: an approval above
    // the bill only means the insurer pays the whole bill (claimSplit.js
    // gives the insurer min(approved, bill)).
    ? { expected: Math.max(0, round2(billTotal - amountNum)), diff: round2(deductionTotal - Math.max(0, round2(billTotal - amountNum))) }
    : null;

  const canDenyWithCounselling = outcome === 'denied' && auth.kind === 'initial';
  const isApproval = outcome === 'approved' || outcome === 'partial';
  const canSave = outcome === 'query'
    ? queryText.trim().length > 0
    : outcome === 'denied' ? (auth.kind !== 'final') : amountNum > 0;
  const headOptions = [...new Set([...(claim.running_bill?.heads ?? []).map((h) => h.head), ...CLAIM_HEADS])].sort(byHeadOrder);

  const save = async () => {
    setError('');
    if (outcome === 'query') {
      // A payer query is not a decision: it is logged against this
      // authorisation through the existing queries endpoint, which marks the
      // authorisation 'query' and moves the claim to *_query (§1.4).
      try {
        await queryMutation.mutateAsync({
          stage: auth.kind === 'final' ? 'final' : 'preauth', authorization_id: auth.id,
          received_at: new Date().toISOString(), query_text: queryText.trim(),
        });
        onClose();
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
      return;
    }
    try {
      await decideMutation.mutateAsync({
        authId: auth.id,
        input: {
          status: outcome,
          approved_amount: isApproval ? amountNum : undefined,
          external_ref: externalRef.trim() || undefined,
          decision_document_id: decisionDocumentId ?? undefined,
          remarks: remarks.trim() || undefined,
          deductions: deductions.filter((d) => Number(d.amount) > 0).map(({ _key: _k, ...d }) => d),
          counselling_note: canDenyWithCounselling ? counsellingNote.trim() || undefined : undefined,
        },
      });
      onClose();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Record decision — {auth.kind} authorisation</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FormField label="Outcome" className="md:col-span-2">
              <SegmentedControl
                options={[
                  { value: 'approved', label: 'Approved' },
                  { value: 'partial', label: 'Partial' },
                  ...(auth.kind !== 'final' ? [{ value: 'denied' as const, label: 'Denied' }] : []),
                  ...(auth.status === 'sent' ? [{ value: 'query' as const, label: 'Query' }] : []),
                ]}
                value={outcome}
                onChange={(v) => setOutcome(v as Outcome)}
              />
            </FormField>
            {isApproval && (
              <FormField label="Approved amount ₹" required>
                <Input type="number" min={0} value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} />
              </FormField>
            )}
            <FormField label="Decided at">
              <div className="pt-1.5 text-[12.5px] text-ink-muted">Stamped by the server when you save</div>
            </FormField>
            {outcome === 'query' && (
              <FormField label="Query from the payer" required className="md:col-span-2">
                <Textarea value={queryText} onChange={(e) => setQueryText(e.target.value)} rows={3} placeholder="What the TPA / insurer is asking for" />
              </FormField>
            )}
            {outcome !== 'query' && (<>
            <FormField label="Insurer / TPA reference">
              <Input value={externalRef} onChange={(e) => setExternalRef(e.target.value)} />
            </FormField>
            <FormField label={isApproval ? 'Approval letter' : 'Decision letter'} className="md:col-span-2">
              <DocumentUploadField
                uhid={claim.uhid ?? ''} category="Pre-auth Approval"
                uploadedFileName={decisionDocumentName}
                onUploaded={(id) => { setDecisionDocumentId(id); setDecisionDocumentName('Uploaded'); }}
              />
            </FormField>
            {canDenyWithCounselling && (
              <FormField label="Counselling note (patient converts to self-pay)" className="md:col-span-2">
                <Textarea value={counsellingNote} onChange={(e) => setCounsellingNote(e.target.value)} rows={2} />
              </FormField>
            )}
            <FormField label="Remarks" className="md:col-span-2">
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} />
            </FormField>
            </>)}
          </div>

          {isApproval && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
                  Deductions listed in the letter (become patient share)
                </div>
                <Button size="sm" variant="ghost" onClick={addDeduction}>+ Add deduction</Button>
              </div>
              {deductions.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Head</TableHead><TableHead>Reason code</TableHead><TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount ₹</TableHead>
                      <TableHead title="A deduction in the authorisation letter always becomes patient share (design §5.3)">Recover from patient</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deductions.map((d) => (
                      <TableRow key={d._key}>
                        <TableCell>
                          <NativeSelect value={d.head ?? ''} onChange={(e) => updateDeduction(d._key, { head: e.target.value })} className="h-7 text-[12px]">
                            <option value="">Head…</option>
                            {headOptions.map((h) => <option key={h} value={h}>{headLabel(h)}</option>)}
                          </NativeSelect>
                        </TableCell>
                        <TableCell><Input list="claim-reason-codes" value={d.reason_code ?? ''} onChange={(e) => updateDeduction(d._key, { reason_code: e.target.value })} className="h-7 w-20 text-[12px]" /></TableCell>
                        <TableCell><Input value={d.description ?? ''} onChange={(e) => updateDeduction(d._key, { description: e.target.value })} className="h-7 text-[12px]" /></TableCell>
                        <TableCell className="text-right"><Input type="number" min={0} value={d.amount || ''} onChange={(e) => updateDeduction(d._key, { amount: Number(e.target.value) })} className="h-7 w-24 text-right text-[12px]" /></TableCell>
                        <TableCell className="text-center"><input type="checkbox" checked readOnly disabled aria-label="Recovered from patient" /></TableCell>
                        <TableCell><Button size="sm" variant="ghost" onClick={() => removeDeduction(d._key)}>✕</Button></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-semibold">
                      <TableCell colSpan={3}>Total deductions</TableCell>
                      <TableCell className="text-right">{money(deductionTotal)}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableBody>
                </Table>
              )}
              <datalist id="claim-reason-codes">
                {REASON_CODES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </datalist>
              {reconciliation && (
                <div className="mt-2 rounded-[var(--radius)] bg-muted px-3 py-2 text-[11.5px]">
                  {amountNum >= (billTotal as number)
                    ? <>Approved {money(amountNum)} covers the whole bill {money(billTotal as number)} — nothing goes to the patient beyond any deductions listed</>
                    : <>Bill {money(billTotal as number)} − approved {money(amountNum)} = {money(reconciliation.expected)}</>}
                  {Math.abs(reconciliation.diff) < 0.01 ? (
                    <Badge variant="success" className="ml-2">✓ reconciles</Badge>
                  ) : (
                    <Badge variant="warn" className="ml-2">Gap {money(reconciliation.diff)} — goes to patient as unallocated</Badge>
                  )}
                </div>
              )}
            </div>
          )}

          {error && <div className="text-[12px] text-danger-ink">{error}</div>}

          <div className="flex justify-end gap-2">
            <DialogPrimitive.Close asChild><Button variant="ghost">Cancel</Button></DialogPrimitive.Close>
            <Button loading={decideMutation.isPending || queryMutation.isPending} disabled={!canSave} onClick={save}>
              {outcome === 'query' ? 'Log query' : 'Save decision'} → status: {resultingStatus(auth.kind, outcome)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
